// Componente Interacciones para usuarios autenticados
// Replica la funcionalidad de la versión mobile (ListaAllInateraction)
// Muestra el historial de interacciones con filtros por tipo

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiHeart, FiAtSign, FiUserPlus, FiUserMinus, FiRefreshCw } from 'react-icons/fi';
import { useInteractionsQuery } from '../../hooks/useInteractions';
import { interactionDescription, interactionIconColor, interactionIconType} from '../../constants/humanQualities';
import toast from 'react-hot-toast';
import { defaultImage } from '../../constants/defaultImage';

// Mapeo de iconos de Ionicons a react-icons (Fi)
const iconMap = {
  'eye': FiEye,
  'heart': FiHeart,
  'at': FiAtSign,
  'person-add': FiUserPlus,
  'person-remove': FiUserMinus,
};

// Función helper para formatear fecha (similar a ShowDateText del mobile)
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Si es hoy
  if (date.toDateString() === today.toDateString()) {
    return 'Hoy';
  }
  
  // Si es ayer
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Ayer';
  }
  
  // Otherwise show full date
  return date.toLocaleDateString('es-ES', { 
    day: 'numeric', 
    month: 'long',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  });
};

/**
 * Componente Interacciones
 * 
 * Replica la funcionalidad de ListaAllInateraction del mobile
 * Muestra lista de interacciones con filtros (Todos, Visitas, Likes)
 */
const Interacciones = () => {
  const navigate = useNavigate();
  const { data: interactionList, isLoading, refetch, error } = useInteractionsQuery();
  const [selectType, setSelectType] = useState(0);
  const [filteredInteractions, setFilteredInteractions] = useState([]);

  // Manejo de refresh: recarga datos
  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Efecto para aplicar filtros cuando cambian los datos o el tipo seleccionado
  useEffect(() => {
    console.log('[Interacciones] Filtrando interacciones por tipo:', selectType);
    
    if (!interactionList) {
      setFilteredInteractions([]);
      return;
    }

    let filtered = interactionList;
    
    // type: 0 = Todos, 1 = Visitas, 2 = Likes
    if (selectType === 1) {
      // Filtrar solo visitas (type_interaction === 1)
      filtered = interactionList.filter(interaction => interaction.type_interaction === 1);
    } else if (selectType === 2) {
      // Filtrar solo likes (type_interaction === 2)
      filtered = interactionList.filter(interaction => interaction.type_interaction === 2);
    }
    // type === 0 muestra todos
    
    console.log('[Interacciones] Interacciones filtradas:', filtered.length);
    setFilteredInteractions(filtered);
  }, [selectType, interactionList]);

  // Función para manejar el cambio de tipo
  const handleTypeChange = useCallback((type) => {
    setSelectType(type);
  }, []);

  // Navega a la pantalla de perfil del remitente usando location.state para no exponer UUID
  const handleOpenFriendProfile = useCallback((friendId) => {
    navigate('/friend/profile', { state: { friendId } });
  }, [navigate]);

  // Manejo de errores
  if (error) {
    toast.error(error.message || "Error al obtener interacciones");
  }

  // Subcomponente para renderizar cada interacción
  const InteractionItem = ({ data }) => {
    const IconComponent = iconMap[interactionIconType[data.type_interaction]] || FiHeart;
    const colors = interactionIconColor[data.type_interaction] || interactionIconColor[0];
    const gradientStyle = {
      background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
    };

    return (
      <div className="flex items-start justify-between mx-4 mt-4 p-5 bg-neutral-800 rounded-3xl transition-colors">
        <div className="flex flex-row items-start gap-4">
          {/* Avatar del remitente */}
          <img
            src={data.remitente_image || defaultImage}
            alt={data.remitente_full_name || 'Usuario'}
            className="w-[70px] h-[70px] rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleOpenFriendProfile(data.remitente)}
            onError={(e) => {
              e.target.src = defaultImage;
            }}
          />

          <div>
            <div>
              <p className="text-white text-lg font-bold">
                {data.remitente_full_name}
              </p>
              <p className="text-white text-lg">
                {interactionDescription[data.type_interaction] || 'interacción'}
              </p>
              <span className="text-neutral-400 text-base opacity-80">
                {formatDate(data.updated_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Icono con gradiente según tipo de interacción */}
        <div
          style={gradientStyle}
          className="w-[50px] h-[50px] flex items-center justify-center rounded-full"
        >
          <IconComponent size={30} color="white" />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Header de la página */}
      <div className='sticky top-0 z-10 backdrop-blur-md'>
        <div className="h-20 p-4 flex items-center justify-between border-b border-neutral-800 bg-neutral-900">
          <div>
            <h2 className="text-2xl font-bold text-white">
              Interacciones
            </h2>
            <p className="text-neutral-400">
              Tus conexiones y actividad social.
            </p>
          </div>

          {/* Botón de refresh */}
          <div className="flex justify-end">
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="flex items-center gap-2 p-2 bg-neutral-700 text-white rounded-lg hover:bg-neutral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiRefreshCw className={isLoading ? 'animate-spin' : ''} size={20}/>
            </button>
          </div>
        </div>

        {/* Botones de filtro - Tabpanel style */}
        <div className="h-12 flex gap-1 p-1">
          <button
            onClick={() => handleTypeChange(0)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              selectType === 0 
                ? 'bg-neutral-700 text-white shadow-md' 
                : 'bg-neutral-950 text-gray-400 hover:bg-neutral-900'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => handleTypeChange(1)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              selectType === 1 
                ? 'bg-neutral-700 text-white shadow-md' 
                : 'bg-neutral-950 text-gray-400 hover:bg-neutral-900'
            }`}
          >
            Visitas
          </button>
          <button
            onClick={() => handleTypeChange(2)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              selectType === 2 
                ? 'bg-neutral-700 text-white shadow-md' 
                : 'bg-neutral-950 text-gray-400 hover:bg-neutral-900'
            }`}
          >
            Likes
          </button>
        </div>

      </div>

      {/* Lista de interacciones */}
      <div className=''>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FiRefreshCw className="animate-spin text-4xl text-neutral-500 mb-4" />
            <p className="text-neutral-600 dark:text-neutral-400">Cargando...</p>
          </div>
        ) : (
          <div>
            {filteredInteractions && filteredInteractions.length > 0 ? (
              filteredInteractions.map((interaction, i) => (
                <InteractionItem key={interaction.id || i} data={interaction} />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <FiHeart className="text-4xl text-neutral-400 mb-4" />
                <p className="text-neutral-500 dark:text-neutral-400">
                  {interactionList && interactionList.length > 0 && filteredInteractions.length === 0
                    ? 'No hay interacciones de este tipo.'
                    : 'No hay interacciones aún.'
                  }
                </p>
              </div>
            )}
            <div style={{ height: '200px', backgroundColor: 'transparent' }} />
          </div>
        )}
      </div>
    </>
  );
};

export default Interacciones;
